/* eslint-disable no-undef */
import { createElement, useEffect } from "react";
import { Navigate } from "react-router-dom";
import useCurrentEmployee from "./hooks/useCurrentEmployee";
import { MODULE_BY_PATH } from "./data/modules";
import { useParams, useNavigate } from "react-router-dom";

// Import useAuthStore hook from the correct location
import useAuthStore from "./data/Stores/Authstore";

const pagesContext = require.context("./pages", true, /\.(js|jsx)$/);
const screensContext = require.context("./screens", true, /\.(js|jsx)$/);

const resolveModuleFromContext = (context, resolvedPageName) => {
  const normalized = `${resolvedPageName || ""}`
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");
  const candidateKeys = [
    `./${normalized}.jsx`,
    `./${normalized}.js`,
    `./${normalized}/index.jsx`,
    `./${normalized}/index.js`,
  ];

  const availableKeys = new Set(context.keys());
  const hit = candidateKeys.find((key) => availableKeys.has(key));

  if (!hit) {
    return null;
  }

  const loaded = context(hit);
  return loaded?.default || loaded;
};

const PageRender = () => {
  // Retrieve page, id, and step from URL params
  const { page, id, step } = useParams();
  const { currentEmployee, loading: employeeLoading } = useCurrentEmployee();
  const moduleId = MODULE_BY_PATH[`/${page}`] || ({ "employee-dashboard": "dashboard", chat: "chats", calendar: "attendance" })[page];
  const escape2 = [
    "employeespage",
    "instructorPage",
    "new-employee",
    "about",
    "our-team",
    "contact-us",
    "sign-up",
    "my-tasks",
    "time-management",
    "add-new",
    "performance-position",
    "new-evaluation",
    "leave-types",
    "approval-flow",
    "approved-flow",
    "admins",
    "permission",
    "new-permission",
    "departments",
    "levels",
    "level-details",
    "view-details",
    "cash-types",
    "leave-flow",
    "position",
    "role",
    "media",
    "company",
    "leave-flow-details",
    "new-permission",
    "department-details",
    "levelview-details",
    "positionview-details",
    "roleview-details",
    "permissionview-details",
    "companyview-details",
    "org-chart",
    "chart-builder",
    "tools",
    "media-details",
    "recruitmentflow",
    "candidate-details",
    "recruitmentpage",
    "card-request",
    "requestTable",
    "request-details",
    "folder-page",
    "open-folder",
    "notification",
    "notification-details",
    "evaluation-details",
    "branches",
    "branchview-details",
    "allQueries",
    "new-query",
    "query-details",
    "notifications",
    "staff-birthdays",
    "work-anniversaries",
    "holidays",
    "recruitment",
    "subscription",
    "holiday-details",
    "career-grading",
    "project-details",
  ];
  const navigate = useNavigate();

  // Use the useAuthStore hook to access authentication-related state
  const { auth, errors, clearErrors, isAuth } = useAuthStore();

  // Define generatePage function inside PageRender
  const generatePage = (resolvedPageName, folderName) => {
    const context = folderName === "screens" ? screensContext : pagesContext;
    const component = resolveModuleFromContext(context, resolvedPageName);

    if (component) {
      return createElement(component);
    }

    console.error("Error loading page:", {
      page: resolvedPageName,
      folder: folderName,
    });

    if (folderName === "screens") {
      return (
        <Navigate
          to="/login"
          replace
        />
      );
    }

    // Avoid redirect loop when dashboard itself cannot resolve.
    if (resolvedPageName === "dashboard") {
      return (
        <div className="p-6 text-sm text-[#B42318] font-Outfit">
          Unable to load dashboard page.
        </div>
      );
    }

    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  };

  useEffect(() => {
    // Redirect to the homepage if the user is not authenticated
    if (auth?.isAuth) {
      if (errors?.errorText) {
        if (page !== "login" && page !== "register") {
          navigate("/");
        }
        clearErrors();
      }
    }
    // Redirect to the homepage if the user is authenticated and tries to access login or register pages
    if (auth?.isAuth) {
      if (page === "login" || page === "register") {
        navigate("/");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, auth?.isAuth, navigate, errors?.errorText]);

  if (!employeeLoading && currentEmployee?.isContractor && page !== "contract-access" && (!moduleId || !currentEmployee.modulePermissions?.includes(moduleId))) {
    return <Navigate to="/contract-access" replace />;
  }


  if (page === "company-settings" && (id === "leave-types" || id === "view-details")) {
    return <Navigate to="/requests" replace />;
  }

  // Construct the page name based on URL params and available escape routes
  let pageName = "";
  if (step) {
    pageName = page === "company-settings" && id === "levelview-details"
      ? `${page}/${id}`
      : `${page}/${id}/${"[id]"}`;
  } else if (id) {
    if (
      (page === "home" && escape2.includes(id)) ||
      (page === "dashboard" && escape2.includes(id)) ||
      (page === "employee" && escape2.includes(id)) ||
      (page === "employee-dashboard" && escape2.includes(id)) ||
      (page === "instructor" && escape2.includes(id)) ||
      (page === "productivity" && escape2.includes(id)) ||
      (page === "performance-evaluation" && escape2.includes(id)) ||
      (page === "recruitement" && escape2.includes(id)) ||
      (page === "company-settings" && escape2.includes(id)) ||
      (page === "leave-types" && escape2.includes(id)) ||
      (page === "requests" && escape2.includes(id)) ||
      (page === "resources" && escape2.includes(id)) ||
      (page === "folder-page" && escape2.includes(id)) ||
      (page === "notification" && escape2.includes(id)) ||
      (page === "query-management" && escape2.includes(id))

      // (page === "levels" && escape2.includes(id))
    ) {
      pageName = `${page}/${id}`;
    } else {
      pageName = `${page}/${"[id]"}`;
    }
  } else {
    pageName = `${page}`;
  }
  // Call generatePage with the constructed pageName and determine the folder based on user authentication status
  const authScreens = ["login", "sign-up", "signup", "register", "forgot-password", "reset-password"];
  const folder = !isAuth && authScreens.includes(page) ? "screens" : "pages";

  return generatePage(pageName, folder);
};

export default PageRender;

// /* eslint-disable no-undef */
// import { createElement, useEffect } from "react";
// import { useContext } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import useAuthStore from "./data/Stores/Authstore";

// const generatePage = (pageName, folder) => {
//   const component = () => require(`./${folder}/${pageName}`).default;
//   try {
//     return createElement(component());
//   } catch (error) {
//     // return <ErrorPage />;
//   }
// };

// const PageRender = () => {
//   const { page, id, step } = useParams();
//   const escape2 = ["home", "about"],
//     navigate = useNavigate();

//   useEffect(() => {
//     if (!auth?.isAuth) {
//       if (errors?.errorText) {
//         if (page !== "login" && page !== "register") {
//           navigate("/");
//         }
//         clearErrors();
//       }
//     }
//     if (auth?.isAuth) {
//       if (page === "login" || page === "register") {
//         navigate("/");
//       }
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [page, auth?.isAuth, navigate, errors?.errorText]);

//   // if (general?.isLoading && users.isLoading) return <Loader />;

//   let pageName = "";
//   if (step) {
//     pageName = `${page}/${id}/${"[id]"}`;
//   } else if (id) {
//     if (
//       (page === "home" && escape2.includes(id)) ||
//       (page === "about" && escape2.includes(id))
//     ) {
//       pageName = `${page}/${id}`;
//     } else {
//       pageName = `${page}/${"[id]"}`;
//     }
//   } else {
//     pageName = `${page}`;
//   }
//   return generatePage(pageName, isAuth ? "pages" : "screens");
// };

// export default PageRender;
